import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { PlayCircleOutline } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useFeature } from '@next-box/feature-flags';
import { useEffect } from 'react';
import { FeatureFlags } from '#enums/featureFlags.ts';
import { ScreenFormatType } from '#enums/mediaTypes.ts';
import { buildVideoPaths } from '#helpers/buildVideoPaths.ts';
import { mapScreenFormatTypeToSectionType } from '#helpers/enumMappers.ts';
import { getVideos } from './helpers/getVideos.ts';
import { type MovieData, type Props, type SeasonData, type TvData } from './types.ts';

const queries = {
  [ScreenFormatType.Episode]: "\nquery GetEpisodeVideos(\n  $id: ID!\n) {\n  episode(id: $id) {\n    ...EpisodeVideos\n  }\n}\n\nfragment EpisodeVideos on Episode {\n  videos {\n    name\n    key\n    site\n    type\n  }\n}\n",
  [ScreenFormatType.Movie]: "\nquery GetMovieVideos(\n  $id: ID!\n) {\n  movie(id: $id) {\n    ...MovieVideos\n  }\n}\n\nfragment MovieVideos on Movie {\n  videos {\n    name\n    key\n    site\n    type\n  }\n}\n",
  [ScreenFormatType.Season]: "\nquery GetSeasonVideos(\n  $id: ID!\n) {\n  season(id: $id) {\n    ...SeasonVideos\n  }\n}\n\nfragment SeasonVideos on Season {\n  videos {\n    name\n    key\n    site\n    type\n  }\n}\n",
  [ScreenFormatType.Tv]: "\nquery GetTvVideos(\n  $id: ID!\n) {\n  tv(id: $id) {\n    ...TvVideos\n  }\n}\n\nfragment TvVideos on Tv {\n  videos {\n    name\n    key\n    site\n    type\n  }\n}\n",
};

export const VideoButton = ({
  action = 'preview',
  component,
  id,
  pathIds,
  setActivePreview,
  setActiveVideo,
  sx,
  title,
  type,
  videoCount,
  videoTypes,
}: Props) => {
  const feature = useFeature(FeatureFlags.ExoticPinkPtarmigan);
  const query = queries[type];

  const { execute, result } = useQuery<MovieData | SeasonData | TvData>(query, {
    loading: true,
  });

  useEffect(() => {
    void execute({ variables: { id } }, { data: { initiator: 'VideoButton' } });

    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  const videos = getVideos(result?.data);

  if (videos.length === 0 && !feature) {
    return;
  }

  return (
    // @ts-expect-error e.stopPropagation is valid to use
    <IconButton
      aria-controls={`${String(id)}-video`}
      aria-label="video"
      component={component}
      edge="start"
      onClick={e => {
        e.stopPropagation();

        if (action === 'preview' && setActivePreview) {
          setActivePreview({ id, type: mapScreenFormatTypeToSectionType(type) });
        } else if (action === 'video' && setActiveVideo) {
          setActiveVideo({
            id,
            pathIds,
            src: buildVideoPaths(videos, { count: videoCount, types: videoTypes }),
            title,
            type,
          });
        }
      }}
      sx={{
        color: theme => theme.palette.grey[100],
        left: 'calc(50% - 1.75rem)',
        position: 'absolute',
        top: 'calc(50% - 2.35rem)',
        ...sx,
      }}
    >
      <PlayCircleOutline sx={{ filter: 'drop-shadow(0px 1px 1px rgb(0 0 0 / 0.4))', fontSize: '4rem' }} />
    </IconButton>
  );
};
